// =============================================================================
// Blocos por categoria
//
// O heatmap categoria × mês, de um centro de custo (Painel do Gestor) ou de
// todos (aba Despesa da Visão financeira), e os blocos consolidados da Despesa,
// pedidos em 27/10 "iguais aos do Painel do Gestor". Os do Painel partem das
// linhas orçamentárias; estes partem da coordenada (lib/categoriasConsolidadas),
// porque juntam todas as áreas e um lançamento rateado entra em cada uma.
// Com ~35 categorias na empresa, os gráficos mostram as 10 maiores; as
// tabelas, todas, com rolagem.
// =============================================================================

import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { buscarOrcadoRealizado } from "../lib/api.ts";
import { linhasHeatmap, type CelulaHeatmap } from "../lib/categoriasPainel.ts";
import {
  situacaoCategoria,
  type CategoriaConsolidada,
  type CentroDaCategoria,
} from "../lib/categoriasConsolidadas.ts";
import { moeda, moedaCompacta, percentual } from "../lib/formato.ts";
import { COR, Pilula, TooltipRico, Vazio } from "./ui.tsx";
import type { ModalLancamentos } from "../paginas/ModalLancamentos.tsx";

const LIMITE_GRAFICO = 10;

/** O que o modal de lançamentos abre: uma coordenada de um mês. */
export type AlvoLancamentos = Parameters<typeof ModalLancamentos>[0]["linha"];

/** A coordenada da categoria num CC, ou em todos (`cc` nulo), para o modal. */
export function alvoDaCategoria(
  cat: CategoriaConsolidada,
  cc: CentroDaCategoria | null,
  competencia: string,
): AlvoLancamentos {
  const v = cc ?? cat;
  return {
    centro_custo_id: cc?.centro_custo_id ?? null,
    centro_custo_codigo: cc?.codigo ?? "",
    centro_custo_nome: cc?.nome ?? "Todos os centros de custo",
    categoria_id: cat.categoria_id,
    categoria_nome: cat.nome,
    competencia,
    orcado_centavos: v.orcado.toString(),
    realizado_centavos: v.realizado.toString(),
    pct_execucao: v.orcado === 0n ? "0" : (Number((v.realizado * 10000n) / v.orcado) / 100).toFixed(2),
  };
}

const nomeCategoria = (c: CategoriaConsolidada) => `${c.codigo ? `${c.codigo} · ` : ""}${c.nome}`;

/** "e mais N categorias, somando R$ X", embaixo do gráfico que cortou em 10. */
function Resto({ itens, valor }: { itens: CategoriaConsolidada[]; valor: (c: CategoriaConsolidada) => bigint }) {
  if (itens.length === 0) return null;
  const soma = itens.reduce((t, c) => t + valor(c), 0n);
  return (
    <p className="mt-2 text-[11.5px] text-tinta-fraca">
      e mais {itens.length} {itens.length === 1 ? "categoria" : "categorias"}, somando{" "}
      <span className="num">{moeda(soma.toString())}</span> — todas estão em “Orçado × realizado por categoria”.
    </p>
  );
}

/**
 * Gastos por categoria — as 10 que mais gastaram no período, em barras. A
 * barra fica vermelha acima do orçado da categoria, e amarela sem orçado.
 */
export function BarrasCategorias({ itens, varios }: { itens: CategoriaConsolidada[]; varios: boolean }) {
  const comGasto = itens.filter((c) => c.realizado > 0n).sort((a, b) => (b.realizado > a.realizado ? 1 : -1));
  if (comGasto.length === 0) return <Vazio titulo={`Nenhum gasto realizado ${varios ? "no período" : "no mês"}`} />;

  const mostrar = comGasto.slice(0, LIMITE_GRAFICO);
  const maior = mostrar[0]!.realizado;
  const total = comGasto.reduce((t, c) => t + c.realizado, 0n);
  return (
    <>
      <ul>
        {mostrar.map((c) => {
          const estourou = c.orcado > 0n && c.realizado > c.orcado;
          const semOrcamento = c.orcado === 0n;
          const largura = maior === 0n ? 0 : Number((c.realizado * 1000n) / maior) / 10;
          return (
            <li key={c.chave} className="border-t border-linha py-2 first:border-t-0">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-[13px] font-medium" title={nomeCategoria(c)}>{c.nome}</span>
                <span className="num shrink-0 text-[12.5px] font-bold">{moeda(c.realizado.toString())}</span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-superficie-2">
                <div
                  className={`h-full rounded-full ${estourou ? "bg-risco-tinta" : semOrcamento ? "bg-atencao-tinta" : "bg-azul-medio"}`}
                  style={{ width: `${largura}%` }}
                />
              </div>
              <p className="num mt-1 text-[11px] text-tinta-fraca">
                {percentual(total === 0n ? 0 : Number((c.realizado * 10000n) / total) / 100)}{" "}
                {varios ? "do período" : "do mês"} ·{" "}
                {semOrcamento ? (
                  <span className="font-semibold text-atencao-tinta">sem orçamento</span>
                ) : (
                  <>de {moeda(c.orcado.toString())} orçados</>
                )}
                {estourou && <span className="font-semibold text-risco-tinta"> · acima do teto</span>}
              </p>
            </li>
          );
        })}
      </ul>
      <Resto itens={comGasto.slice(LIMITE_GRAFICO)} valor={(c) => c.realizado} />
    </>
  );
}

/** Saldo por categoria — as 10 de menor saldo: barra à esquerda do zero é estouro. */
export function SaldoCategorias({ itens }: { itens: CategoriaConsolidada[] }) {
  if (itens.length === 0) return <Vazio titulo="Sem categorias no período" />;
  const dados = [...itens]
    .sort((a, b) => (a.saldo === b.saldo ? 0 : a.saldo < b.saldo ? -1 : 1))
    .slice(0, LIMITE_GRAFICO)
    .map((c) => ({ nome: c.nome, saldo: Number(c.saldo) / 100 }));

  return (
    <div style={{ height: Math.max(dados.length * 34, 120) }}>
      <ResponsiveContainer>
        <BarChart data={dados} layout="vertical" margin={{ left: 4, right: 12, top: 4, bottom: 4 }}>
          <XAxis
            type="number"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10.5, fill: "#6b7789" }}
            tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))}
          />
          <YAxis type="category" dataKey="nome" width={150} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#48566e" }} />
          <ReferenceLine x={0} stroke="#c3cbd9" />
          <Tooltip
            cursor={{ fill: "rgba(16,40,80,0.04)" }}
            content={({ active, payload }) => {
              const p = payload?.[0]?.payload as { nome: string; saldo: number } | undefined;
              return active && p ? (
                <TooltipRico
                  titulo={p.nome}
                  itens={[{
                    rotulo: p.saldo >= 0 ? "Saldo disponível" : "Acima do teto",
                    valor: Math.abs(p.saldo).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
                  }]}
                />
              ) : null;
            }}
          />
          <Bar dataKey="saldo" radius={2} maxBarSize={18}>
            {dados.map((x, i) => <Cell key={i} fill={x.saldo >= 0 ? COR.positivo : COR.negativo} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Orçado × realizado por categoria, todas, com rolagem. A categoria abre na
 * própria linha mostrando cada centro de custo; com um mês só, cada um tem o
 * caminho até os lançamentos (o detalhe por coordenada é de um mês).
 */
export function TabelaCategoriasConsolidada({
  itens,
  competencia,
  varios,
  onVer,
}: {
  itens: CategoriaConsolidada[];
  competencia: string;
  varios: boolean;
  onVer: (alvo: AlvoLancamentos) => void;
}) {
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set());
  if (itens.length === 0) {
    return <Vazio titulo="Nenhuma categoria com orçado ou gasto no período" />;
  }
  const alternar = (chave: string) => setAbertas((s) => {
    const n = new Set(s);
    if (!n.delete(chave)) n.add(chave);
    return n;
  });
  const total = itens.reduce(
    (t, c) => ({ orcado: t.orcado + c.orcado, realizado: t.realizado + c.realizado, saldo: t.saldo + c.saldo }),
    { orcado: 0n, realizado: 0n, saldo: 0n },
  );
  const celulas = (v: { orcado: bigint; realizado: bigint; saldo: bigint }, classe = "") => (
    <>
      <td className={`num px-2 py-2 text-right whitespace-nowrap ${classe}`}>{v.orcado === 0n ? <span className="text-tinta-fraca">—</span> : moeda(v.orcado.toString())}</td>
      <td className={`num px-2 py-2 text-right whitespace-nowrap ${classe}`}>{moeda(v.realizado.toString())}</td>
      <td className={`num px-2 py-2 text-right whitespace-nowrap ${v.saldo < 0n ? "font-semibold text-risco-tinta" : ""} ${classe}`}>{moeda(v.saldo.toString())}</td>
    </>
  );

  return (
    <div className="thin-scroll max-h-[560px] overflow-auto">
      <table className="w-full min-w-[760px] text-[13px]">
        <thead className="sticky top-0 z-10 bg-superficie">
          <tr className="text-[11px] tracking-wide text-tinta-fraca uppercase">
            <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Categoria</th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Orçado</th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Realizado</th>
            <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold" title="Orçado menos realizado e comprometido">Saldo</th>
            <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Status</th>
            <th className="border-b-2 border-azul px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {itens.map((c) => {
            const aberta = abertas.has(c.chave);
            const s = situacaoCategoria(c);
            return (
              <Fragment key={c.chave}>
                <tr className="cursor-pointer border-b border-linha hover:bg-superficie-2" onClick={() => alternar(c.chave)}>
                  <td className="px-2 py-2">
                    <span aria-hidden className="mr-1.5 inline-block w-3 text-tinta-fraca">{aberta ? "▾" : "▸"}</span>
                    {c.codigo && <span className="num mr-1.5 text-tinta-fraca">{c.codigo}</span>}
                    {c.nome}
                    {!aberta && c.centros.length > 1 && (
                      <span className="ml-1.5 text-[11px] whitespace-nowrap text-tinta-fraca">({c.centros.length} centros de custo)</span>
                    )}
                  </td>
                  {celulas(c)}
                  <td className="px-2 py-2">{s && <Pilula tom={s.tom}>{s.rotulo}</Pilula>}</td>
                  <td className="px-2 py-2 text-right">
                    {!varios && c.realizado + c.comprometido !== 0n && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onVer(alvoDaCategoria(c, null, competencia)); }}
                        className="text-[12px] font-semibold whitespace-nowrap text-azul-medio hover:underline"
                      >
                        Ver lançamentos
                      </button>
                    )}
                  </td>
                </tr>
                {aberta && c.centros.map((cc) => {
                  const sc = situacaoCategoria(cc);
                  return (
                    <tr key={cc.centro_custo_id} className="border-b border-linha/60 bg-superficie-2/40 text-[12.5px] text-tinta-media">
                      <td className="py-1.5 pr-2 pl-[30px]">
                        <span className="num mr-1.5 text-tinta-fraca">{cc.codigo}</span>
                        {cc.nome}
                      </td>
                      {celulas(cc, "py-1.5")}
                      <td className="px-2 py-1.5">{sc && <Pilula tom={sc.tom}>{sc.rotulo}</Pilula>}</td>
                      <td className="px-2 py-1.5 text-right">
                        {!varios && cc.realizado + cc.comprometido !== 0n && (
                          <button
                            type="button"
                            onClick={() => onVer(alvoDaCategoria(c, cc, competencia))}
                            className="text-[12px] font-semibold whitespace-nowrap text-azul-medio hover:underline"
                          >
                            Ver lançamentos
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="sticky bottom-0 bg-superficie-2 font-bold">
          <tr className="border-t-2 border-linha-forte">
            <td className="px-2 py-2">Total</td>
            {celulas(total)}
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Principais desvios: as 10 categorias mais acima do orçado, com o gasto sem orçamento. */
export function DesviosCategorias({
  itens,
  competencia,
  varios,
  onVer,
}: {
  itens: CategoriaConsolidada[];
  competencia: string;
  varios: boolean;
  onVer: (alvo: AlvoLancamentos) => void;
}) {
  const acima = itens
    .filter((c) => c.realizado > c.orcado)
    .sort((a, b) => {
      const da = a.realizado - a.orcado, db = b.realizado - b.orcado;
      return db > da ? 1 : db < da ? -1 : 0;
    });
  if (acima.length === 0) {
    return (
      <Vazio
        titulo={`Nenhuma categoria acima do orçado ${varios ? "no período" : "no mês"}`}
        dica="Aparecem aqui as categorias cujo realizado passou do orçado, somando todos os centros de custo, da maior diferença para a menor."
      />
    );
  }
  const mostrar = acima.slice(0, LIMITE_GRAFICO);
  return (
    <>
      <div className="thin-scroll overflow-x-auto">
        <table className="w-full min-w-[620px] text-[13px]">
          <thead>
            <tr className="text-[11px] tracking-wide text-tinta-fraca uppercase">
              <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Categoria</th>
              <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Realizado / Orçado</th>
              <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Desvio</th>
              <th className="border-b-2 border-azul px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {mostrar.map((c) => {
              const desvio = c.realizado - c.orcado;
              const pct = c.orcado === 0n ? null : Number((desvio * 10000n) / c.orcado) / 100;
              const acimaEm = c.centros.filter((x) => x.realizado > x.orcado).map((x) => x.codigo);
              return (
                <tr key={c.chave} className="border-b border-linha">
                  <td className="px-2 py-2">
                    {c.nome}
                    {acimaEm.length > 0 && (
                      <span className="block text-[11px] text-tinta-fraca">
                        acima em {acimaEm.length === 1 ? "1 centro de custo" : `${acimaEm.length} centros de custo`}: {acimaEm.join(", ")}
                      </span>
                    )}
                  </td>
                  <td className="num px-2 py-2 text-right">
                    {moeda(c.realizado.toString())}
                    <span className="block text-[11px] text-tinta-fraca">
                      {c.orcado === 0n ? "sem orçamento" : <>de {moeda(c.orcado.toString())}</>}
                    </span>
                  </td>
                  <td className="num px-2 py-2 text-right font-semibold text-risco-tinta">
                    {moeda(desvio.toString())}
                    {pct !== null && <span className="block text-[11px] font-normal text-tinta-fraca">{percentual(pct)} acima</span>}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {!varios && (
                      <button
                        type="button"
                        onClick={() => onVer(alvoDaCategoria(c, null, competencia))}
                        className="text-[12px] font-semibold whitespace-nowrap text-azul-medio hover:underline"
                      >
                        Ver lançamentos
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Resto itens={acima.slice(LIMITE_GRAFICO)} valor={(c) => c.realizado - c.orcado} />
    </>
  );
}

/**
 * Heatmap categoria × mês.
 *
 * ⚠️ Vem de `/api/orcado-realizado?exercicio=`, não da resposta do painel: a
 * do painel traz o heatmap de UMA competência, e a matriz do protótipo precisa
 * dos doze meses. A RLS recorta para os centros de custo do gestor, então
 * pedir o exercício inteiro não abre nada que ele já não visse.
 *
 * Lia `/api/execucao/categorias`, que parte da linha orçamentária — e por isso
 * não mostrava a categoria que gastou sem orçamento, justamente a que o gestor
 * mais precisa ver. A chave de cache é a mesma da aba Orçado vs Realizado,
 * que pede o mesmo dado. Com `centroCustoId` nulo, soma todos os CCs (Despesa).
 */
export function HeatmapCategoriaMes({
  exercicio,
  centroCustoId,
  ate,
}: {
  exercicio: number;
  centroCustoId: string | null;
  ate: string;
}) {
  const q = useQuery({
    queryKey: ["orcado-realizado", exercicio, "despesa"],
    queryFn: () => buscarOrcadoRealizado(exercicio, "despesa"),
  });

  const ateMes = Number(ate.slice(5, 7));
  const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"]
    .slice(0, ateMes);

  const linhas = useMemo(
    () => linhasHeatmap(q.data?.linhas ?? [], centroCustoId, ateMes),
    [q.data, centroCustoId, ateMes],
  );

  if (q.isPending) return <div className="h-32 animate-pulse rounded-lg bg-superficie-2" />;
  if (linhas.length === 0) return <Vazio titulo="Sem orçamento nem gasto neste exercício" />;

  const cor = (c: CelulaHeatmap) => {
    if (c.tipo === "vazio") return "bg-superficie-2 text-tinta-fraca";
    if (c.tipo === "sem_orcamento") return "bg-superficie text-atencao-tinta ring-1 ring-inset ring-atencao-linha";
    if (c.valor > 100) return "bg-risco-bg text-risco-tinta";
    if (c.valor >= 70) return "bg-atencao-bg text-atencao-tinta";
    return "bg-ok-bg text-ok-tinta";
  };
  const texto = (c: CelulaHeatmap) =>
    c.tipo === "vazio" ? "—" : c.tipo === "sem_orcamento" ? "sem" : `${Math.round(c.valor)}%`;

  return (
    <>
      <div className={`thin-scroll overflow-x-auto ${centroCustoId === null ? "max-h-[560px] overflow-y-auto" : ""}`}>
        <table className="w-full min-w-[640px] text-[12px]">
          <thead className={centroCustoId === null ? "sticky top-0 z-10 bg-superficie" : undefined}>
            <tr className="text-[11px] text-tinta-fraca uppercase">
              <th className="px-2 py-1.5 text-left font-semibold">Categoria</th>
              {meses.map((m) => (
                <th key={m} className="px-1 py-1.5 text-center font-semibold">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.chave}>
                <td className="px-2 py-1 text-[12.5px]">{l.nome}</td>
                {l.celulas.map((c, i) => (
                  <td key={i} className="p-0.5">
                    <span
                      title={c.tipo === "sem_orcamento" ? "Gasto sem orçamento no mês" : undefined}
                      className={`block rounded-md px-1 py-2 text-center text-[11px] font-semibold ${cor(c)}`}
                    >
                      {texto(c)}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-[11.5px] text-tinta-fraca">
        <span className="rounded-md bg-ok-bg px-2 py-0.5 text-ok-tinta">até 70%</span>
        <span className="rounded-md bg-atencao-bg px-2 py-0.5 text-atencao-tinta">70–100%</span>
        <span className="rounded-md bg-risco-bg px-2 py-0.5 text-risco-tinta">acima de 100%</span>
        <span className="rounded-md bg-superficie px-2 py-0.5 text-atencao-tinta ring-1 ring-inset ring-atencao-linha">
          sem · gasto sem orçamento no mês
        </span>
      </div>
    </>
  );
}
